import {RequestInfo} from "@khan/shared-core";

export default function printContents(
    editorElement: HTMLElement,
    printedDocumentTitle: string,
    isIframe?: boolean,
) {
    if (RequestInfo.isExtension || isIframe) {
        // The extension's iframe doesn't support printing the content window,
        // so we'll open a new window and print that instead.
        printInNewWindow(toHtmlString(editorElement, printedDocumentTitle));
    } else {
        printWithFrame(toHtmlString(editorElement, printedDocumentTitle));
    }
}

export const toHtmlString = (
    editorElement: HTMLElement,
    printedDocumentTitle: string,
): string => {
    const clonedElement = editorElement.cloneNode(true) as HTMLElement;

    stripAttribute(clonedElement, "contenteditable");
    stripAttribute(clonedElement, "data-testid");
    stripElementsMatching(clonedElement, "[data-remove-on-print]");

    const cssLinkTags = Array.from(
        document.querySelectorAll("link[rel=stylesheet]"),
    )
        .map((tag) => tag.outerHTML)
        .join("\n");

    const styleTags = Array.from(document.querySelectorAll("style"))
        .map((tag) => tag.outerHTML)
        .join("\n");

    const outerHTML = clonedElement.outerHTML;

    return `<!doctype html><html>
      <head>
        <title>${printedDocumentTitle}</title>
        ${cssLinkTags}
        ${styleTags}
      </head>
      <body>${outerHTML}</body>
    </html>`;
};

const printInNewWindow = (html: string) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
        return;
    }

    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
    };
};

const printWithFrame = (html: string) => {
    const printFrame = document.createElement("iframe");
    printFrame.srcdoc = html;
    printFrame.width = "0";
    printFrame.height = "0";

    document.body?.appendChild(printFrame);

    printFrame.onload = () => {
        if (!printFrame.contentWindow) {
            return;
        }

        printFrame.contentWindow.print();

        printFrame.contentWindow.addEventListener("afterprint", () => {
            // After print dialog is closed (even if dismissed with "Cancel"),
            // remove this temporary iframe from the document.
            document.body?.removeChild(printFrame);
        });
    };
};

const stripAttribute = (element: HTMLElement, attributeName: string) => {
    if (element.hasAttribute(attributeName)) {
        element.removeAttribute(attributeName);
    }

    for (const childElement of element.querySelectorAll(`[${attributeName}]`)) {
        childElement.removeAttribute(attributeName);
    }
};

const stripElementsMatching = (element: HTMLElement, selector: string) => {
    if (element.matches(selector)) {
        element.remove();
        return;
    }

    for (const childElement of element.querySelectorAll(selector)) {
        childElement.remove();
    }
};
